import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useBranding } from "../components/Layout";
import { MailOpen, Mail, Send, CheckCircle2, ClipboardList, Layers, List as ListIcon, Tag as TagIcon, BarChart3, ChevronDown } from "lucide-react";

// Topic config — keep in sync with backend MESSAGE_TAGS in server.py.
const TAG_LABELS = {
  pricing: "Pricing",
  showings: "Showings",
  contract: "Contract",
  inspection: "Inspection",
  prep: "Prep",
  closing: "Closing",
  marketing: "Marketing",
  other: "Other",
};
const TAG_COLORS = {
  pricing: { bg: "#FBECE8", fg: "#9C3D2A" },     // terracotta-ish
  showings: { bg: "#E5F0EA", fg: "#2B4C3F" },    // forest green
  contract: { bg: "#EFE7F4", fg: "#5B3A77" },    // plum
  inspection: { bg: "#FFF4D6", fg: "#7A5800" },  // amber
  prep: { bg: "#E1EEF7", fg: "#23597D" },        // slate blue
  closing: { bg: "#E8F2EE", fg: "#1F6B4F" },     // deep green
  marketing: { bg: "#FBE7EF", fg: "#922F5A" },   // rose
  other: { bg: "#EAE6DD", fg: "#57534E" },       // bone/gray
};
const TAG_KEYS = Object.keys(TAG_LABELS);

const PERIOD_OPTIONS = [
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "year", label: "Last 12 months" },
  { id: "all", label: "All time" },
];

export default function AdminMessages() {
  const branding = useBranding();
  const askLabel = `Ask ${branding.agent_first_name}`;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grouped, setGrouped] = useState(true);
  const [tagFilter, setTagFilter] = useState("all"); // 'all' | one of TAG_KEYS

  const load = async () => {
    try { const { data } = await api.get("/admin/messages"); setItems(data.messages || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try { await api.post(`/admin/messages/${id}/read`); setItems((xs) => xs.map((x) => x.id === id ? { ...x, read: true } : x)); } catch { /* swallow */ }
  };

  const onReplied = (id, reply) => {
    setItems((xs) => xs.map((x) => x.id === id ? { ...x, read: true, admin_reply: reply } : x));
  };

  const onTagChanged = (id, tag) => {
    setItems((xs) => xs.map((x) => x.id === id ? { ...x, tag, tag_overridden: true } : x));
  };

  const filtered = useMemo(() => {
    if (tagFilter === "all") return items;
    return items.filter((m) => (m.tag || "other") === tagFilter);
  }, [items, tagFilter]);

  const groups = useMemo(() => {
    const by = new Map();
    for (const m of filtered) {
      const key = m.household_id || m.user_id;
      if (!by.has(key)) by.set(key, []);
      by.get(key).push(m);
    }
    return Array.from(by.entries())
      .map(([hid, msgs]) => {
        msgs.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        const first = msgs[0];
        const names = Array.from(new Set(msgs.map((m) => m.user_name).filter(Boolean))).join(" & ");
        return {
          hid,
          msgs,
          headerName: names || first?.user_name || "—",
          property: first?.property_address || "",
          unread: msgs.filter((m) => !m.read).length,
          latest: first?.created_at || "",
        };
      })
      .sort((a, b) => (b.latest || "").localeCompare(a.latest || ""));
  }, [filtered]);

  // Counts per tag for the filter chips (driven by full inbox, not filtered).
  const counts = useMemo(() => {
    const c = Object.fromEntries(TAG_KEYS.map((k) => [k, 0]));
    for (const m of items) c[m.tag || "other"] = (c[m.tag || "other"] || 0) + 1;
    return c;
  }, [items]);

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="overline">Admin · Messages</span>
          <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
            Seller questions
          </h1>
          <p style={{ color: "var(--text-muted)" }}>Anything sellers sent you via "{askLabel}" or directly from a prep task. Auto-tagged by topic so you can spot patterns.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button data-testid="msg-group-toggle-on" onClick={() => setGrouped(true)} className={grouped ? "btn-secondary" : "btn-ghost"} style={{ padding: "8px 12px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Layers size={14} /> Group by client
          </button>
          <button data-testid="msg-group-toggle-off" onClick={() => setGrouped(false)} className={!grouped ? "btn-secondary" : "btn-ghost"} style={{ padding: "8px 12px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <ListIcon size={14} /> All in one list
          </button>
        </div>
      </div>

      <InsightsPanel />

      {/* Tag filter chips */}
        {/* An empty inbox used to show every topic reading zero, which is a
            filing cabinet with nothing in it. Topics appear as questions do.
            The one you are filtered on stays put even if it empties, so the
            row never rearranges itself under your cursor. */}
        {items.length > 0 && (
      <div style={{ marginTop: 24, display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="tag-filter-chips">
        <FilterChip
          label="All"
          count={items.length}
          active={tagFilter === "all"}
          onClick={() => setTagFilter("all")}
          testid="tag-chip-all"
        />
          {TAG_KEYS.filter((t) => (counts[t] || 0) > 0 || tagFilter === t).map((t) => (
          <FilterChip
            key={t}
            label={TAG_LABELS[t]}
            count={counts[t] || 0}
            active={tagFilter === t}
            color={TAG_COLORS[t]}
            onClick={() => setTagFilter(t)}
            testid={`tag-chip-${t}`}
          />
        ))}
      </div>
        )}

      {loading ? (
        <div style={{ color: "var(--text-muted)", marginTop: 24 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="card" style={{ padding: 32, color: "var(--text-muted)", marginTop: 24 }} data-testid="messages-empty">
          No questions yet. They'll appear here (and in your inbox) when sellers send one.
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 32, color: "var(--text-muted)", marginTop: 16 }} data-testid="messages-filter-empty">
          No <b>{TAG_LABELS[tagFilter]}</b> questions yet — try another topic.
        </div>
      ) : grouped ? (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 18 }} data-testid="grouped-messages">
          {groups.map((g) => (
            <div key={g.hid} className="card" style={{ overflow: "hidden" }} data-testid={`msg-group-${g.hid}`}>
              <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>{g.headerName}</div>
                    {g.property && <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{g.property}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {g.unread > 0 && <span className="badge" style={{ background: "var(--primary)", color: "#fff" }}>{g.unread} new</span>}
                    <span className="badge">{g.msgs.length} message{g.msgs.length === 1 ? "" : "s"}</span>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {g.msgs.map((m) => (
                  <MessageCard key={m.id} m={m} markRead={markRead} onReplied={onReplied} onTagChanged={onTagChanged} compact />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 14 }} data-testid="messages-list">
          {filtered.map((m) => (
            <MessageCard key={m.id} m={m} markRead={markRead} onReplied={onReplied} onTagChanged={onTagChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Insights panel ---------- */
function InsightsPanel() {
  const [period, setPeriod] = useState("90d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/admin/messages/insights?period=${period}`)
      .then(({ data }) => { if (alive) setData(data); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period]);

  const top = useMemo(() => {
    if (!data) return [];
    return (data.breakdown || []).filter((b) => b.count > 0).slice(0, 5);
  }, [data]);
  const max = top.reduce((acc, b) => Math.max(acc, b.count), 0) || 1;

  return (
    <div className="card" style={{ marginTop: 24, padding: 22 }} data-testid="insights-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <BarChart3 size={16} style={{ color: "var(--primary)" }} />
          <span className="font-serif" style={{ fontSize: 18, fontWeight: 500 }}>Top topics</span>
          {data && <span style={{ fontSize: 13, color: "var(--text-muted)" }}>· {data.total} question{data.total === 1 ? "" : "s"}</span>}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select data-testid="insights-period" className="input-field" style={{ padding: "6px 10px", fontSize: 13, minWidth: 160 }} value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIOD_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <button data-testid="insights-toggle" onClick={() => setCollapsed((c) => !c)} className="btn-ghost" style={{ fontSize: 13, padding: "6px 10px", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <ChevronDown size={14} style={{ transform: collapsed ? "rotate(-90deg)" : "none", transition: "transform 0.2s" }} />
            {collapsed ? "Show" : "Hide"}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div style={{ marginTop: 16 }} data-testid="insights-body">
          {loading ? (
            <div style={{ color: "var(--text-muted)", fontSize: 14 }}>Crunching numbers…</div>
          ) : top.length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontSize: 14 }} data-testid="insights-empty">No questions in this window yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {top.map((b) => {
                const c = TAG_COLORS[b.tag] || TAG_COLORS.other;
                const pct = Math.round((b.count / max) * 100);
                return (
                  <div key={b.tag} data-testid={`insights-row-${b.tag}`}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: c.fg }}>{TAG_LABELS[b.tag]}</span>
                      <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{b.count}</span>
                    </div>
                    <div style={{ width: "100%", height: 8, borderRadius: 99, background: "var(--surface-alt)", overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: c.fg, transition: "width 0.4s ease" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Filter chip ---------- */
function FilterChip({ label, count, active, color, onClick, testid }) {
  const bg = active ? (color?.fg || "var(--text)") : (color?.bg || "var(--surface-alt)");
  const fg = active ? "#fff" : (color?.fg || "var(--text)");
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        padding: "6px 12px",
        borderRadius: 999,
        border: active ? `1px solid ${color?.fg || "var(--text)"}` : "1px solid var(--border)",
        background: bg,
        color: fg,
        fontSize: 13,
        fontWeight: 500,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        cursor: "pointer",
        transition: "background 0.15s, color 0.15s",
      }}
    >
      {label}
      <span style={{ fontSize: 11, opacity: 0.85 }}>{count}</span>
    </button>
  );
}

/* ---------- Tag badge with override ---------- */
function TagBadge({ tag, msgId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const t = tag || "other";
  const c = TAG_COLORS[t] || TAG_COLORS.other;

  const change = async (next) => {
    if (next === t) { setOpen(false); return; }
    setBusy(true);
    try {
      await api.post(`/admin/messages/${msgId}/tag`, { tag: next });
      onChanged(next);
    } catch { /* keep silent — UI badge unchanged on failure */ }
    finally { setBusy(false); setOpen(false); }
  };

  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        data-testid={`tag-badge-${msgId}`}
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        style={{
          background: c.bg, color: c.fg, padding: "3px 9px", borderRadius: 999, fontSize: 11,
          fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", border: "none",
          display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer",
        }}
      >
        <TagIcon size={9} /> {TAG_LABELS[t]}
      </button>
      {open && (
        <div
          onMouseLeave={() => setOpen(false)}
          data-testid={`tag-menu-${msgId}`}
          style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 20, background: "#fff", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", padding: 6, minWidth: 140 }}
        >
          {TAG_KEYS.map((k) => {
            const cc = TAG_COLORS[k];
            return (
              <button
                key={k}
                type="button"
                data-testid={`tag-option-${msgId}-${k}`}
                onClick={() => change(k)}
                style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
                  padding: "6px 8px", border: "none", background: k === t ? "var(--surface-alt)" : "transparent",
                  borderRadius: 6, cursor: "pointer", fontSize: 13,
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 99, background: cc.fg }} />
                {TAG_LABELS[k]}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}

function MessageCard({ m, markRead, onReplied, onTagChanged, compact }) {
  const isPrepTask = m.source === "prep_task";
  return (
    <div
      className={compact ? "" : "card"}
      style={{
        padding: compact ? "20px 20px" : 24,
        borderTop: compact ? "1px solid var(--border)" : undefined,
        borderLeft: compact ? (m.read ? "none" : "3px solid var(--primary)") : (m.read ? "1px solid var(--border)" : "3px solid var(--primary)"),
        background: compact ? "transparent" : undefined,
      }}
      data-testid={`message-${m.id}`}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {!m.read && <span className="badge badge-success" style={{ background: "var(--primary)", color: "#fff" }}>New</span>}
            <TagBadge tag={m.tag} msgId={m.id} onChanged={(next) => onTagChanged(m.id, next)} />
            {isPrepTask && <span className="badge" style={{ background: "#FBECE8", color: "var(--secondary)", display: "inline-flex", alignItems: "center", gap: 4 }}><ClipboardList size={10} /> From prep task</span>}
            <span className="font-serif" style={{ fontSize: 20, fontWeight: 500 }}>{m.subject}</span>
          </div>
          <div style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4 }}>
            From <b style={{ color: "var(--text)" }}>{m.user_name}</b> · <a href={`mailto:${m.user_email}`} style={{ color: "var(--primary)" }}>{m.user_email}</a>
            {m.property_address && ` · ${m.property_address}`}
          </div>
          <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>{new Date(m.created_at).toLocaleString()}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <a href={`mailto:${m.user_email}?subject=Re: ${encodeURIComponent(m.subject)}`} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 13 }} data-testid={`reply-email-${m.id}`}>Reply by email only</a>
          {!m.read && <button onClick={() => markRead(m.id)} className="btn-ghost" data-testid={`mark-read-${m.id}`} style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}><MailOpen size={14} /> Mark read</button>}
          {m.read && !m.admin_reply && <span style={{ color: "var(--text-muted)", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}><Mail size={14} /> Read</span>}
        </div>
      </div>

      <div style={{ marginTop: 16, padding: 16, background: "var(--surface-alt)", borderRadius: 12, whiteSpace: "pre-wrap", color: "var(--text)" }}>{m.body}</div>

      {m.admin_reply ? (
        <ReplyShown reply={m.admin_reply} attached={isPrepTask} />
      ) : (
        <ReplyComposer messageId={m.id} isPrepTask={isPrepTask} onReplied={(r) => onReplied(m.id, r)} />
      )}
    </div>
  );
}

function ReplyShown({ reply, attached }) {
  return (
    <div data-testid="admin-reply-shown" style={{ marginTop: 14, padding: 16, border: "1px solid var(--primary)", borderRadius: 12, background: "#fff" }}>
      <div className="overline" style={{ color: "var(--primary)", display: "inline-flex", alignItems: "center", gap: 6 }}>
        <CheckCircle2 size={12} /> Your reply
      </div>
      <div style={{ marginTop: 6, whiteSpace: "pre-wrap", color: "var(--text)" }}>{reply.body}</div>
      <div style={{ marginTop: 8, color: "var(--text-muted)", fontSize: 12 }}>
        Sent {reply.replied_at ? new Date(reply.replied_at).toLocaleString() : ""}
        {attached ? " · posted on the seller's prep task" : " · emailed to the seller"}
      </div>
    </div>
  );
}

function ReplyComposer({ messageId, isPrepTask, onReplied }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/messages/${messageId}/reply`, { body: text });
      onReplied(data.reply);
      setBody("");
      setOpen(false);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Couldn't send — try again.");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          data-testid={`reply-open-${messageId}`}
          onClick={() => setOpen(true)}
          className="btn-secondary"
          style={{ padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Send size={14} /> {isPrepTask ? "Reply & post on their prep task" : "Reply inside PorchLight"}
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14 }} data-testid={`reply-composer-${messageId}`}>
      <textarea
        data-testid={`reply-input-${messageId}`}
        className="input-field"
        rows={4}
        placeholder={isPrepTask ? "Your guidance will appear on this task in their Prep Guide and be emailed to them." : "Your reply will be emailed to the seller."}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        disabled={busy}
      />
      {err && <div style={{ color: "var(--secondary)", fontSize: 13, marginTop: 6 }} data-testid={`reply-err-${messageId}`}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button
          type="button"
          data-testid={`reply-send-${messageId}`}
          onClick={send}
          className="btn-primary"
          disabled={busy || !body.trim()}
          style={{ padding: "9px 16px", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Send size={14} /> {busy ? "Sending…" : "Send reply"}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setBody(""); setErr(""); }}
          className="btn-ghost"
          style={{ padding: "9px 12px", fontSize: 13 }}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
